import {logFathomEvent, setLoginAnalytics} from 'notability-services-common-frontend'
import * as Sentry from '@sentry/react'
import {CodeResponse, useGoogleLogin} from '@react-oauth/google'
import {OAuthProvider, Platform, getOAuthState, localesToLanguageCode} from 'notability-services-common-universal'

import {SsoButtonTemplate} from './SsoButtonTemplate'
import {config} from '../config'
import {useCustomRouter} from '../util/useCustomRouter'
import {useCallback} from 'react'

export type SSOButtonProps = {
  setShowOAuthError: (value: boolean) => void
  isFromWebApp: boolean
}

export function GoogleLoginButton({setShowOAuthError, isFromWebApp}: SSOButtonProps) {
  const router = useCustomRouter()

  const handleGoogleLoginSuccess = async (codeResponse: CodeResponse) => {
    setShowOAuthError(false)
    try {
      const state = getOAuthState(
        Platform.WEB,
        OAuthProvider.GOOGLE,
        localesToLanguageCode(navigator.languages),
        isFromWebApp
      )
      const res = await fetch(`${config.API_SERVICE_URL}/google/sign-in`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({code: codeResponse.code, state})
      })

      setLoginAnalytics({
        type: OAuthProvider.GOOGLE
      })

      if (res.status === 200) {
        logFathomEvent('SSO Complete - Google')
        router.pushLoginComplete()
      } else if (res.status === 401) {
        setShowOAuthError(true)
      } else if (res.status === 403) {
        router.pushEmailLinkConfirmation('sign-in')
      }
    } catch (error) {
      Sentry.captureException(error)
      setShowOAuthError(true)
    }
  }

  const handleGoogleLoginError = () => {
    setShowOAuthError(true)
  }

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: handleGoogleLoginError,
    flow: 'auth-code',
    ux_mode: 'popup',
    redirect_uri: 'postmessage'
  })

  const doGoogleLogin = useCallback(() => {
    logFathomEvent('SSO Open - Google')
    googleLogin()
  }, [googleLogin])

  return <SsoButtonTemplate provider={OAuthProvider.GOOGLE} onClick={doGoogleLogin} />
}
